import { useEffect, useState, useCallback, useRef } from 'react'
import { TFunction, useTranslation } from 'react-i18next'
import { BlockTypes } from 'enum/block-type.enum'
import GetLoadPercentage from '../api/administration/blocks/GetLoadPercentage'
import { sleep } from './others.helper'

interface ScormLoadPercentageProps {
  block: {
    id?: number
    type?: BlockTypes
    processing?: boolean
    loadPercentage?: number
  }
  delay?: number
  onFinish?: () => void
  onLoad?: (percentage: number) => void
  onError?: (errorMessage: string) => void
}

const translateScormError = (
  t: TFunction,
  errorCode?: string | number,
  errorInfo?: string
) => {
  if (!errorCode) return ''
  const translationKey = `lessons.contentBlocks.scormBlock.errors.${errorCode}`
  if (String(errorCode) === '702' && errorInfo) {
    return t(translationKey, { file: errorInfo })
  }
  return t(translationKey)
}

export const useScormLoadPercentage = ({
  block,
  delay = 5000,
  onFinish,
  onLoad,
  onError,
}: ScormLoadPercentageProps) => {
  const { t } = useTranslation()
  const { type } = block
  const isScorm = type === BlockTypes.BlockScorm
  const showPercentageBase = isScorm && block.processing
  const onFinishRef = useRef(onFinish)
  const onLoadRef = useRef(onLoad)
  const onErrorRef = useRef(onError)
  useEffect(() => {
    onFinishRef.current = onFinish
  }, [onFinish])
  useEffect(() => {
    onLoadRef.current = onLoad
  }, [onLoad])
  useEffect(() => {
    onErrorRef.current = onError
  }, [onError])

  const [showPercentage, setShowPercentage] = useState(showPercentageBase)
  const [percentage, setPercentage] = useState(block.loadPercentage)
  const [hasError, setHasError] = useState(false)
  const [errorMessage, setErrorMessage] = useState<string | undefined>()

  const reload = useCallback(() => {
    if (block.id)
      return GetLoadPercentage(block.id)
        .then((data) => {
          setPercentage(data.loadPercentage)
          return data
        })
        .catch(() => undefined)
  }, [block.id])

  useEffect(() => {
    let mount = true
    const init = async () => {
      let lastPercentage = block.loadPercentage || 0
      let diff: number | undefined
      let interval = true
      let shouldCallOnFinish = false

      while (mount && interval) {
        const res = await reload()
        if (res) {
          const {
            processed,
            processing,
            loadPercentage,
            haveErrors,
            errorCode,
            errorInfo,
          } = res
          if (haveErrors) {
            const translatedError = translateScormError(t, errorCode, errorInfo)

            setHasError(true)
            setErrorMessage(translatedError)
            setPercentage(100)
            setShowPercentage(true)
            onErrorRef.current?.(translatedError || '')
            break
          }

          if (processed && !processing) {
            shouldCallOnFinish = true
            interval = false
          } else {
            interval = processing
          }

          const currentDiff = loadPercentage - lastPercentage || 1
          diff = diff ? currentDiff + diff / 2 : currentDiff
        }
        const sleepTime = diff ? delay / diff : delay
        mount && setShowPercentage(interval)
        lastPercentage = res?.loadPercentage || 0
        onLoadRef.current?.(lastPercentage)

        if (showPercentageBase === false) {
          mount = false
          break
        }

        if (interval) {
          await sleep(sleepTime)
        }
      }
      if (mount && shouldCallOnFinish) {
        onFinishRef.current?.()
      }
    }

    if (showPercentageBase || (isScorm && showPercentageBase === false)) init()
    return () => {
      mount = false
    }
  }, [
    reload,
    isScorm,
    showPercentageBase,
    block.id,
    block.loadPercentage,
    delay,
  ])

  return {
    showPercentage: showPercentageBase && showPercentage,
    percentage,
    reload,
    hasError: isScorm && hasError,
    errorMessage,
  }
}
